import type { TourStep } from '../types';
import { finishStep } from './common';

/**
 * Студия показывает только текущий этап, поэтому тур собирается из того, что есть на экране:
 * на направлениях — карточки направлений, на материалах — плитки материалов и т. д.
 */
export const layoutStudioSteps: TourStep[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Студия серии',
    content: 'Здесь серия проходит путь от идеи до готовых листов. Система работает сама и останавливается там, где нужно ваше решение. Тур покажет то, что есть на текущем этапе.',
  },
  {
    target: 'header[aria-label="Серия"]',
    title: 'Шапка серии',
    content: 'Название, тематика, способ создания и текущий этап со статусом: «Выполняется» — ждать, «Нужно решение» — серия ждёт вас.',
  },
  {
    target: 'nav[aria-label="Этапы студии"]',
    title: 'Этапы',
    content: 'Направление → Сценарии → Круги → Материалы → Макеты → Приёмка. Клик по пройденному этапу открывает его сводку. «Авто» — этап утвердила система.',
  },
  {
    target: 'button[aria-label="Расход студии"]',
    title: 'Расход',
    content: 'Сколько потрачено из лимита задания. Внутри — «Потрачено», «В резерве», «Свободно». Перед каждым платным шагом цена видна на кнопке.',
  },
  {
    target: '[role="radiogroup"][aria-label="Режим проверки"]',
    title: 'Быстро или пошагово',
    content: '«Быстро» — сценарии и материалы без замечаний утверждает система. «Пошагово» — каждый этап утверждаете вы.',
  },
  {
    target: '[data-tour="studio-header-actions"]',
    title: 'Задание, модели, композиции',
    content: '«Задание и готовые PDF» — паспорт серии с путём до каталога и продаж. Там же модели ИИ и библиотека композиций.',
  },
  {
    target: '[data-tour="studio-step-failure"]',
    title: 'Шаг не удался',
    content: 'Причина написана словами. Порядок: «Повторить шаг» → «Сменить модель» → при нехватке лимита «Поднять лимит». Если повторяется — «Подробности» администратору.',
  },
  {
    target: '[data-tour="queue-review"]',
    title: 'Нужна сверка запроса',
    content: 'Связь с моделью оборвалась, и неизвестно, выполнен ли платный запрос. Сначала сверка в «Интеграции ИИ», потом повтор — иначе можно заплатить дважды.',
  },
  {
    target: '[data-direction-id]',
    title: 'Направление',
    content: 'Эскиз листа из шести кругов, идея, стиль, тон, палитра, персонаж и надписи. Клик по эскизу — крупно, «Сюжеты 6 кругов» — что в каждом круге.',
  },
  {
    target: '[data-tour="direction-pick"]',
    title: 'Выбрать',
    content: 'Отмечает основное направление. По его эскизу нарисуется печатный лист кругов.',
  },
  {
    target: '[data-tour="direction-revise"]',
    title: 'Изменить одно направление',
    content: 'Поменять стиль, персонажа или тон одного направления, не трогая остальные. Цена — на кнопке в окне.',
  },
  {
    target: '[data-testid="directions-texts-block"]',
    title: 'Надписи серии',
    content: 'Фразы на 6 кругах, 4 плашках топперов и 8 кружках капкейков. По умолчанию — поздравление повода; любую можно заменить своей.',
  },
  {
    target: '[data-tour="studio-directions-bar"]',
    title: 'Подтвердить выбор',
    content: '«Выбрать это направление» запускает следующий шаг. В «…» — «Предложить другие» и «Изменить и предложить заново».',
  },
  {
    target: '[data-tour="scenario-row"]',
    title: 'Сценарий круга',
    content: 'Композиция, доля героя, сюжет и надпись. «Изменить» — поправить надпись или переписать сюжет одного круга.',
  },
  {
    target: '[data-tour="studio-scenarios-bar"]',
    title: 'Утвердить сценарии',
    content: 'После утверждения рисуется лист кругов. «Выбрать другое направление» — бесплатный возврат.',
  },
  {
    target: '[data-tour="studio-circles-sheet"]',
    title: 'Лист кругов',
    content: 'Шесть кругов с номерами. Проверьте руки, лица, лишние предметы и надписи.',
  },
  {
    target: '[data-tour="circle-fixes"]',
    title: 'Что исправить',
    content: 'Впишите правку к нужному кругу или ко всему листу. Пустые поля круги не трогают — меняется только написанное.',
  },
  {
    target: '[data-tour="studio-circles-bar"]',
    title: 'Утвердить круги',
    content: 'Утверждение режет круги и сразу запускает три платных листа банка: топперы и капкейки. Есть правки — кнопка станет «Исправить круги по правкам».',
  },
  {
    target: '[data-testid="materials-status-strip"]',
    title: 'Состояние материалов',
    content: 'Готово, обязательные, исключённые, проблемные, покрытие по типам и замечания листов.',
  },
  {
    target: '[data-testid="sheet-sources"]',
    title: 'Исходные листы ИИ',
    content: 'Оригинал каждого листа и вырезанные из него материалы. Нарезка ошиблась — «…» → «Перенарезать листы» бесплатно.',
  },
  {
    target: '[data-testid="material-tile"]',
    title: 'Материал',
    content: '«Исправить ИИ» — точечная правка (центы), «Исключить» — убрать из серии, в «…» — загрузить свой файл, закрепить, подробности.',
  },
  {
    target: 'section[aria-label="Предупреждения по листам"]',
    title: 'Замечания листов',
    content: 'Пустые ячейки и брак печати. «Принять как есть» — явное решение оставить.',
  },
  {
    target: '[data-tour="studio-materials-bar"]',
    title: 'Утвердить материалы',
    content: '«Утвердить материалы и собрать» — система разложит материалы на листы A4. «Заполнить дублями» и «Подготовить недостающие» — если чего-то не хватает.',
  },
  {
    target: '[data-testid="series-status-strip"]',
    title: 'Готовая серия',
    content: 'Сколько листов, утверждено, ждут приёмки, принято; ИИ-проверка и замечания. Фильтр «Требуют внимания» покажет листы с замечаниями.',
  },
  {
    target: '[data-tour="series-ai-review"]',
    title: 'ИИ-проверка',
    content: 'По умолчанию не запускается. Клик посчитает резерв и предложит платную проверку всех листов.',
  },
  {
    target: '[data-testid="series-sheet-tile"]',
    title: 'Лист серии',
    content: 'Галочка — выбрать, «Редактировать» — ручной редактор, «…» — PDF, «Исправить картинку ИИ» во всех листах, варианты композиции. Красный бейдж «Есть ошибки проверки» — лист нельзя утвердить.',
  },
  {
    target: '[data-tour="studio-series-bar"]',
    title: 'Утвердить и отправить в ПИМ',
    content: 'Отметьте листы и нажмите главную кнопку — откроется мастер «Создать макеты». В «…» — «Вернуться к материалам», «Проверить серию ИИ», «Отправить на приёмку».',
  },
  finishStep,
];
